import React, { useEffect, useState, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Image } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { router } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getMatchedSponsors, Sponsor } from '../../services/sponsorService';
import PageHeader from '../../components/PageHeader';
import { SkeletonSponsorCard } from '../../components/Skeleton';
import Avatar from '../../components/Avatar';
import SponsorProfileSheet from '../../components/SponsorProfileSheet';
import ProfessionalDetails from '../../components/ProfessionalDetails';

export default function SponsorList() {
  const { t, language } = useLanguage();
  const { userProfile, sessionReady } = useAuth();
  const firstName = userProfile?.full_name?.split(' ')[0] ?? '';
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileSponsor, setProfileSponsor] = useState<Sponsor | null>(null);
  const [profileVisible, setProfileVisible] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (sessionReady && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadSponsors();
    }
  }, [sessionReady]);

  const loadSponsors = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMatchedSponsors();
      setSponsors(data);
      // PRD §5.9 — `sponsor_list_viewed`. Fired only after a successful
      // load so the count reflects screens that actually rendered the
      // list (failed retries don't double-count). The helper takes no
      // metadata — sponsor count would be PII-ish (signal of treatment
      // assignment for small clinics) and the dashboard derives it from
      // the cohort filter.
      import('@/utils/clarityEvents')
        .then(({ logSponsorListViewed }) => logSponsorListViewed())
        .catch(() => {});
    } catch (err) {
      console.error('Error loading sponsors:', err);
      setError(t('sponsors.list.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewAgenda = (sponsor: Sponsor) => {
    router.push(`/sponsors/${sponsor.id}/agenda`);
  };

  const handleOpenProfile = (sponsor: Sponsor) => {
    setProfileSponsor(sponsor);
    setProfileVisible(true);
  };

  const handleCloseProfile = () => {
    setProfileVisible(false);
  };

  const handleScheduleFromProfile = () => {
    setProfileVisible(false);
    if (profileSponsor) {
      setTimeout(() => handleViewAgenda(profileSponsor), 300);
    }
  };

  const professionalDetailLabels = {
    crp: t('sponsors.details.crp'),
    session: t('sponsors.details.session'),
    price: t('sponsors.details.price'),
  };
  const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
  const formatSessionDuration = (minutes: number) =>
    t('sponsors.details.durationMinutes', { minutes: String(minutes) });

  const CalendarIcon = () => (
    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <Path d="M15.1523 3.69238V6.6677" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M7.92969 3.69238V6.6677" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path fillRule="evenodd" clipRule="evenodd" d="M15.683 5.06445L7.40863 5.06527C4.97812 5.06527 3.46094 6.41874 3.46094 8.90651V16.3957C3.46094 18.9229 4.97812 20.3077 7.40863 20.3077L15.6759 20.3068C18.1136 20.3068 19.6235 18.9462 19.6235 16.4576V8.90569C19.6307 6.41792 18.1207 5.06445 15.683 5.06445Z" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M7.52344 11.4375H7.53344" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M7.52344 14.8916H7.53344" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M11.5391 11.4375H11.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M11.5391 14.8916H11.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M15.5391 11.4375H15.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M15.5391 14.8916H15.5491" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader
        title={t('sponsors.list.title')}
        onBack={() => router.back()}
      />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Friendship Illustration + Subtitle — hidden during loading */}
        {!loading && (
          <>
            <View style={styles.illustrationContainer}>
              <Image
                source={require('../../assets/images/friendship.png')}
                style={styles.illustrationImage}
                resizeMode="contain"
              />
            </View>

            {!error && sponsors.length > 0 ? (
              <Text style={styles.subtitle}>
                {firstName
                  ? `${firstName}, achamos ${sponsors.length} ${sponsors.length === 1 ? 'profissional' : 'profissionais'} que combinam com você`
                  : `Achamos ${sponsors.length} ${sponsors.length === 1 ? 'profissional' : 'profissionais'} que combinam com você`}
              </Text>
            ) : !error ? (
              <Text style={styles.subtitle}>{t('sponsors.list.subtitle')}</Text>
            ) : null}
          </>
        )}

        {/* Loading State */}
        {loading && (
          <>
            <SkeletonSponsorCard />
            <SkeletonSponsorCard />
            <SkeletonSponsorCard />
          </>
        )}

        {/* Error State */}
        {error && !loading && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity onPress={loadSponsors} style={styles.retryButton}>
              <Text style={styles.retryButtonText}>{t('sponsors.list.retry')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Empty State */}
        {!loading && !error && sponsors.length === 0 && (
          <Text style={styles.emptyText}>{t('sponsors.list.empty')}</Text>
        )}

        {/* Sponsor Cards */}
        {!loading && !error && sponsors.map((sponsor) => {
          const MAX_CHIPS = 5;
          const visibleInterests = sponsor.interests.slice(0, MAX_CHIPS);
          const overflow = sponsor.interests.length - MAX_CHIPS;

          return (
            <View key={sponsor.id} style={styles.sponsorCard}>
              {/* Header: Avatar + Name/Role + Perfil button */}
              <View style={styles.sponsorHeader}>
                <TouchableOpacity
                  style={styles.avatar}
                  onPress={() => handleOpenProfile(sponsor)}
                  activeOpacity={0.8}
                >
                  <Avatar
                    uri={sponsor.photo_url}
                    size={40}
                    style={{ borderRadius: 12 }}
                  />
                </TouchableOpacity>
                <View style={styles.sponsorInfo}>
                  <Text style={styles.sponsorName}>{sponsor.name}</Text>
                  <Text style={styles.sponsorRole}>
                    {sponsor.role || t('sponsors.list.padrinho')}
                  </Text>
                </View>
                <TouchableOpacity
                  style={styles.profileButton}
                  onPress={() => handleOpenProfile(sponsor)}
                  activeOpacity={0.7}
                >
                  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <Path d="M3 12C3 16.9699 7.02908 21 12 21C16.9709 21 21 16.9699 21 12C21 7.02908 16.9709 3 12 3C7.02908 3 3 7.02908 3 12Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M12.0057 15.6932V11.3936M12 8.35426V8.29102" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                </TouchableOpacity>
              </View>

              {/* Bio — 2 lines max */}
              <Text style={styles.sponsorBio} numberOfLines={2}>
                {sponsor.bio}
              </Text>

              {/* Chips — 2 rows max (5 chips + overflow) */}
              <View style={styles.interestsContainer}>
                {visibleInterests.map((interest, index) => (
                  <View key={index} style={styles.interestPill}>
                    <Text style={styles.interestText}>{interest}</Text>
                  </View>
                ))}
                {overflow > 0 && (
                  <View style={styles.interestPill}>
                    <Text style={styles.interestText}>+{overflow}</Text>
                  </View>
                )}
              </View>

              <ProfessionalDetails
                sponsor={sponsor}
                labels={professionalDetailLabels}
                locale={locale}
                formatSessionDuration={formatSessionDuration}
                withTopDivider
              />

              {/* Schedule Button */}
              <TouchableOpacity
                style={styles.scheduleButton}
                onPress={() => handleViewAgenda(sponsor)}
              >
                <CalendarIcon />
                <Text style={styles.scheduleButtonText}>
                  {t('sponsors.scheduleSession')}
                </Text>
              </TouchableOpacity>
            </View>
          );
        })}

        <View style={{ height: 80 }} />
      </ScrollView>

      {/* Professional Profile Sheet */}
      <SponsorProfileSheet
        sponsor={profileSponsor}
        isVisible={profileVisible}
        onClose={handleCloseProfile}
        onSchedule={handleScheduleFromProfile}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 0,
  },
  illustrationContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 168,
    marginBottom: 16,
    overflow: 'hidden',
  },
  illustrationImage: {
    width: 280,
    height: 280,
  },
  subtitle: {
    fontSize: 20,
    color: '#151515',
    textAlign: 'center',
    marginBottom: 24,
    fontFamily: 'Sentient-Regular',
    lineHeight: 28,
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  errorText: {
    fontSize: 14,
    color: '#757C85',
    textAlign: 'center',
    marginBottom: 16,
    fontFamily: 'Inter_400Regular',
  },
  retryButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#015480',
    borderRadius: 12,
  },
  retryButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
  },
  emptyText: {
    fontSize: 14,
    color: '#757C85',
    textAlign: 'center',
    paddingVertical: 40,
    fontFamily: 'Inter_400Regular',
  },
  sponsorCard: {
    padding: 24,
    backgroundColor: '#F9F8F3',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    marginBottom: 16,
    gap: 16,
  },
  sponsorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 12,
    overflow: 'hidden',
  },
  sponsorInfo: {
    flex: 1,
    gap: 4,
  },
  sponsorName: {
    fontSize: 20,
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    lineHeight: 28,
  },
  sponsorRole: {
    fontSize: 12,
    color: '#6B9EC2',
    fontFamily: 'Inter_700Bold',
    lineHeight: 12,
  },
  profileButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sponsorBio: {
    fontSize: 14,
    color: '#151515',
    lineHeight: 19.6,
    fontFamily: 'Inter_400Regular',
  },
  interestsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  interestPill: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#F0EEE4',
    borderRadius: 17,
  },
  interestText: {
    fontSize: 12,
    color: '#3D3C38',
    fontFamily: 'Inter_500Medium',
    lineHeight: 16.8,
  },
  scheduleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#151515',
    borderRadius: 12,
    height: 48,
    gap: 8,
  },
  scheduleButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    lineHeight: 19.6,
  },
});
